import { useTranslation } from 'react-i18next'

import i18n from '../../i18n'
import { faDigits } from '../../lib/numerals'
import { GOOD_FROM, MID_FROM, wordBand, wordScore } from '../../lib/scoreBand'

/**
 * Per-word pronunciation feedback.
 *
 * This is the whole reason the project uses Pronunciation Assessment rather
 * than plain speech-to-text: a transcript can only say which words were
 * recognised, while this shows how well each one was said.
 *
 * Colour is never the only signal. Each word also carries an accessible
 * label with its score and a distinct underline per error type, so the
 * feedback still reads for a colour-blind learner or in a screen reader.
 */

const t = (key, options) => i18n.t(key, { ns: 'review', ...options })

/* good/warn/bad-tint/-ink are theme tokens (index.css), same bands as the
   overall score ring (lib/scoreBand.js) — a word is not scored on a
   different scale than the exercise it belongs to. */
const BAND_CLASS = {
  good: 'text-good-ink bg-good-tint',
  warn: 'text-warn-ink bg-warn-tint',
  bad: 'text-bad-ink bg-bad-tint underline decoration-bad',
}

function toneFor(word) {
  if (word.errorType === 'Omission') {
    return 'text-slate-400 dark:text-slate-500 line-through decoration-slate-300 dark:decoration-slate-600'
  }
  if (word.errorType === 'Insertion') {
    return 'text-rose-600 dark:text-rose-400 underline decoration-wavy decoration-rose-400'
  }
  // V1-w: the server's `band` (V1-b, pulled toward the weakest phoneme)
  // wins; a cached or older response without it falls back to the local
  // band of Azure's accuracy.
  return BAND_CLASS[wordBand(word)]
}

function errorLabelFor(errorType) {
  const labels = {
    None: t('wordFeedback.errorNone'),
    Mispronunciation: t('wordFeedback.errorMispronunciation'),
    Omission: t('wordFeedback.errorOmission'),
    Insertion: t('wordFeedback.errorInsertion'),
  }
  return labels[errorType] ?? errorType
}

/**
 * The score shown for a word — "—" for an omitted word, never "0".
 *
 * Azure reports accuracy 0 for a word that was never said at all, and
 * showing that as a number tells the learner they pronounced it badly when
 * really they said nothing — design-system.md is explicit that omission is
 * a fourth state, not the bottom of the score range.
 */
function scoreLabelFor(word) {
  if (word.errorType === 'Omission') return '—'
  return t('wordFeedback.scorePercent', { value: faDigits(Math.round(wordScore(word))) })
}

// The legend says where the bands actually start (docs/design-system.md:
// 75/55 since 2026-09-07) — built from the constants, so the text can no
// longer drift from the rule the way «۷۰/۵۰» did. The middle band ends one
// below the good one (۵۵ تا ۷۴), as in the Android result screen.
// A function, so the digits follow the language the page is in now.
const legendValuesOf = () => ({ good: faDigits(GOOD_FROM), mid: faDigits(MID_FROM), midEnd: faDigits(GOOD_FROM - 1) })

export default function WordFeedback({ words = [] }) {
  const { t } = useTranslation('review')
  const legendValues = legendValuesOf()

  if (words.length === 0) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">{t('wordFeedback.unavailable')}</p>
  }

  return (
    <div>
      {/* The script reads left-to-right even though the page does not. */}
      <p className="lesson-text ltr flex flex-wrap gap-x-1.5 gap-y-2 text-lg leading-relaxed">
        {words.map((word, index) => (
          <span
            key={`${word.text}-${index}`}
            title={t('wordFeedback.title', { word: word.text, score: scoreLabelFor(word), error: errorLabelFor(word.errorType) })}
            aria-label={t('wordFeedback.ariaLabel', { word: word.text, score: scoreLabelFor(word), error: errorLabelFor(word.errorType) })}
            className={`rounded px-1 ${toneFor(word)}`}
          >
            {word.text}
          </span>
        ))}
      </p>

      <ul className="mt-4 flex flex-wrap gap-3 text-xs text-slate-500 dark:text-slate-400">
        <li className="flex items-center gap-1.5">
          <span aria-hidden="true" className="size-3 rounded bg-good-tint" /> {t('wordFeedback.legendGood', legendValues)}
        </li>
        <li className="flex items-center gap-1.5">
          <span aria-hidden="true" className="size-3 rounded bg-warn-tint" /> {t('wordFeedback.legendWarn', legendValues)}
        </li>
        <li className="flex items-center gap-1.5">
          <span aria-hidden="true" className="size-3 rounded bg-bad-tint" /> {t('wordFeedback.legendBad', legendValues)}
        </li>
        <li className="flex items-center gap-1.5">
          <span aria-hidden="true" className="size-3 rounded border border-dashed border-slate-300 dark:border-slate-600" />
          {t('wordFeedback.legendOmitted')}
        </li>
      </ul>
    </div>
  )
}
